/**
 * MechaGuard - LevelSelect (主菜单关卡选择)
 *
 * 同时渲染两类关卡：
 *   - 内置关卡（LEVELS）：固定 3 张
 *   - 自定义地图：来自 CustomMapStore，运行时增删
 *
 * 两者共用**唯一一份选中状态** selectedId。点击任意一侧都会让另一侧
 * 失去高亮 —— 否则会出现「两张卡片同时是选中态」，而 startGame()
 * 不知道该听谁的。
 *
 * 卡片内容用 DOM API 逐个构建而不是拼 innerHTML 字符串，
 * 避免玩家给地图起的名字里带特殊字符时破坏结构。
 */

import { LEVELS } from '../maps/MapData.js';
import { balanceConfig } from '../config/BalanceConfig.js';

const MAX_DIFFICULTY = 3;

export class LevelSelect {
  constructor() {
    this.selectedId = LEVELS[0].id;

    /** @type {Map<string, HTMLElement>} id → 可点击卡片 */
    this._buttons = new Map();
    /** @type {object[]} 当前的自定义地图列表 */
    this._customMaps = [];

    this._onSelect = null;
    this._onEdit = null;
    this._onDelete = null;
    this._onNew = null;

    this._builtInRoot = null;
    this._customRoot = null;
  }

  /**
   * @param {{
   *   onSelect?:(id:string)=>void,
   *   onEdit?:(id:string)=>void,
   *   onDelete?:(id:string)=>void,
   *   onNew?:()=>void,
   * }} [opts]
   */
  init({ onSelect, onEdit, onDelete, onNew } = {}) {
    this._onSelect = onSelect || null;
    this._onEdit = onEdit || null;
    this._onDelete = onDelete || null;
    this._onNew = onNew || null;

    this._builtInRoot = document.getElementById('level-select');
    this._customRoot = document.getElementById('custom-map-list');

    const newBtn = document.getElementById('new-map-btn');
    if (newBtn) {
      newBtn.addEventListener('click', () => {
        if (this._onNew) this._onNew();
      });
    }

    this._renderBuiltIns();
    this._renderCustom();
    this._apply();
  }

  /* ===================================================
   * 数据
   * =================================================== */

  /**
   * 更新自定义地图列表（增删改之后由 main.js 调用）。
   * @param {object[]} list
   */
  setCustomMaps(list) {
    this._customMaps = Array.isArray(list) ? list : [];

    // 选中的那张地图被删掉了：退回第一张内置关卡。
    // 不这样做的话 startGame() 会拿到一个已不存在的 id，
    // getLevel() 会静默回退到第一关，玩家看到的是「点 A 却进了 B」。
    if (!this._exists(this.selectedId)) {
      this.selectedId = LEVELS[0].id;
      if (this._onSelect) this._onSelect(this.selectedId);
    }

    this._renderCustom();
    this._apply();
  }

  _exists(id) {
    return LEVELS.some(l => l.id === id)
      || this._customMaps.some(m => m.id === id);
  }

  /* ===================================================
   * 选中状态
   * =================================================== */

  getSelected() {
    return this.selectedId;
  }

  /** @param {string} id */
  setSelected(id) {
    if (this.selectedId === id) return;

    this.selectedId = id;
    this._apply();

    if (this._onSelect) this._onSelect(id);
  }

  _apply() {
    for (const [id, el] of this._buttons) {
      el.classList.toggle('selected', id === this.selectedId);
    }
  }

  /* ===================================================
   * 渲染
   * =================================================== */

  _renderBuiltIns() {
    const root = this._builtInRoot;
    if (!root) return;

    root.innerHTML = '';

    for (const level of LEVELS) {
      root.appendChild(this._buildCard(level));
    }

    this._rebuildButtonIndex();
  }

  _renderCustom() {
    const root = this._customRoot;
    if (!root) return;

    root.innerHTML = '';

    if (this._customMaps.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'custom-empty';
      empty.textContent =
        '还没有自定义地图。点「＋ 新建」画一张 —— 在网格上点击连成通道，'
        + '第一个点是出怪口内侧，最后一点就是基地。';
      root.appendChild(empty);
      this._rebuildButtonIndex();
      return;
    }

    for (const map of this._customMaps) {
      const item = document.createElement('div');
      item.className = 'custom-item';

      item.appendChild(this._buildCard(map));

      const actions = document.createElement('div');
      actions.className = 'custom-item-actions';

      const editBtn = document.createElement('button');
      editBtn.type = 'button';
      editBtn.className = 'btn-mini';
      editBtn.textContent = '✏️ 编辑';
      editBtn.addEventListener('click', () => {
        if (this._onEdit) this._onEdit(map.id);
      });

      const delBtn = document.createElement('button');
      delBtn.type = 'button';
      delBtn.className = 'btn-mini btn-mini--danger';
      delBtn.textContent = '🗑 删除';
      delBtn.addEventListener('click', () => {
        if (this._onDelete) this._onDelete(map.id);
      });

      actions.append(editBtn, delBtn);
      item.appendChild(actions);

      root.appendChild(item);
    }

    this._rebuildButtonIndex();
  }

  /**
   * 两个容器的卡片是一起重建的，所以索引也整体重建一次。
   * 分开维护很容易在删除/新增后留下失效的旧元素引用。
   */
  _rebuildButtonIndex() {
    this._buttons.clear();

    for (const root of [this._builtInRoot, this._customRoot]) {
      if (!root) continue;
      for (const el of root.querySelectorAll('.level-card')) {
        this._buttons.set(el.dataset.id, el);
      }
    }
  }

  /** 构建一张关卡卡片（内置与自定义共用） */
  _buildCard(level) {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'level-card';
    card.dataset.id = level.id;
    card.title = level.subtitle;

    const head = document.createElement('span');
    head.className = 'level-head';

    const icon = document.createElement('span');
    icon.className = 'level-icon';
    icon.textContent = level.icon;

    const name = document.createElement('span');
    name.className = 'level-name';
    name.textContent = level.name;

    const diff = document.createElement('span');
    diff.className = 'level-diff';
    diff.textContent = '★'.repeat(level.difficulty)
      + '☆'.repeat(Math.max(0, MAX_DIFFICULTY - level.difficulty));

    head.append(icon, name, diff);

    const sub = document.createElement('span');
    sub.className = 'level-sub';
    sub.textContent = level.subtitle;

    const stats = document.createElement('span');
    stats.className = 'level-stats';
    // 反映运营覆盖后的总波数（未覆盖时回退到关卡自带默认值）
    let waveTotal = level.totalWaves;
    try {
      const tw = balanceConfig.getWaveOptions().totalWaves;
      if (Number.isFinite(tw)) waveTotal = tw;
    } catch { /* balanceConfig 未初始化时忽略 */ }
    stats.textContent =
      `💰 ${level.startGold}　❤️ ${level.startLives}　🌊 ${waveTotal}`;

    card.append(head, sub, stats);
    card.addEventListener('click', () => this.setSelected(level.id));

    return card;
  }
}
